import { api } from '../api';

export type ActionType = 'email' | 'webhook' | 'database' | 'file_upload' | 'crm_update';
export type ActionStatus = 'pending' | 'approved' | 'rejected' | 'executed';

export interface ActionCitation {
  path: string;
  start_line: number;
  end_line: number;
  text: string;
}

export interface ActionApprovalMetadata {
  required: boolean;
  status: 'not_required' | 'pending' | 'approved' | 'rejected';
  decided_by?: string | null;
  decided_at?: string | null;
  rationale?: string | null;
  metadata: Record<string, unknown>;
}

export interface ActionPolicyDecision {
  policy: string;
  outcome: 'allow' | 'deny' | 'require_approval';
  rationale: string;
  citations: ActionCitation[];
  metadata: Record<string, unknown>;
}

export interface ActionPlanContext {
  plan_id: string;
  step_id: string;
  step_index?: number;
  step_type?: string;
  step_description?: string;
  step_reasoning?: string;
  inputs?: Record<string, unknown>[];
  citations?: ActionCitation[];
  metadata?: Record<string, unknown>;
  approval?: ActionApprovalMetadata;
}

export interface ActionAuditEvent {
  timestamp: string;
  event: string;
  actor: string;
  details: Record<string, unknown>;
}

export interface Action {
  id: string;
  action_type: ActionType;
  status: ActionStatus;
  requires_approval: boolean;
  payload: Record<string, unknown>;
  metadata: Record<string, unknown>;
  audit_log: ActionAuditEvent[];
  reasoning?: string | null;
  citations: ActionCitation[];
  policy_decisions: ActionPolicyDecision[];
  approval: ActionApprovalMetadata;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  approved_by?: string | null;
  approved_at?: string | null;
  rejected_by?: string | null;
  rejected_at?: string | null;
  rejection_reason?: string | null;
  executed_at?: string | null;
  execution_result?: Record<string, unknown> | null;
}

export interface StageActionPayload {
  action_type: ActionType;
  payload: Record<string, unknown>;
  requires_approval?: boolean;
  metadata?: Record<string, unknown>;
  reasoning?: string;
  citations?: ActionCitation[];
  policy_decisions?: ActionPolicyDecision[];
  approval?: ActionApprovalMetadata;
  plan_context?: ActionPlanContext;
}

export interface ApprovalPayload {
  rationale?: string | null;
}

export interface RejectionPayload {
  reason: string;
}

export interface ExecutionPayload {
  metadata?: Record<string, unknown>;
}

export const listActions = async (status?: ActionStatus | 'all'): Promise<Action[]> => {
  const search = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
  const response = await api.get(`api/actions${search}`);
  const data = await response.json<{ actions: Action[] }>();
  return data.actions;
};

export const requestAction = async (payload: StageActionPayload): Promise<Action> => {
  const response = await api.post('api/actions/request', { json: payload });
  const data = await response.json<{ action: Action }>();
  return data.action;
};

export const approveAction = async (
  actionId: string,
  payload: ApprovalPayload = {},
): Promise<Action> => {
  const response = await api.post(`api/actions/${encodeURIComponent(actionId)}/approve`, {
    json: payload,
  });
  const data = await response.json<{ action: Action }>();
  return data.action;
};

export const rejectAction = async (
  actionId: string,
  payload: RejectionPayload,
): Promise<Action> => {
  const response = await api.post(`api/actions/${encodeURIComponent(actionId)}/reject`, {
    json: payload,
  });
  const data = await response.json<{ action: Action }>();
  return data.action;
};

export const executeAction = async (
  actionId: string,
  payload: ExecutionPayload = {},
): Promise<Action> => {
  const response = await api.post(`api/actions/${encodeURIComponent(actionId)}/execute`, {
    json: payload,
  });
  const data = await response.json<{ action: Action }>();
  return data.action;
};
